import { ButtonColors } from '@mui/material';

import type { Theme } from '../../themes/themes';

const createButtonStates = (colors: ButtonColors) => {
  return {
    backgroundColor: colors['background'],
    color: colors['color'],
    boxShadow: `inset 0px 0px 0px 1px ${colors['shadow']}`,

    '&:focus-visible, &.Mui-focusVisible': {
      boxShadow: `inset 0px 0px 0px 1px ${colors['shadow']}`,
      color: colors['colorFocus'],
      outline: `2px solid ${colors['shadowFocus']}`,
      outlineOffset: '2px',
    },
    '&:hover, &.Mui-hover': {
      backgroundColor: colors['backgroundHover'],
      color: colors['colorHover'],
      boxShadow: `inset 0px 0px 0px 1px ${colors['shadowHover']}`,
    },
    '&:active, &.Mui-active, &[aria-current=page], &[aria-current=location]': {
      backgroundColor: colors['backgroundActive'],
      color: colors['colorActive'],
      boxShadow: `inset 0px 0px 0px 1px ${colors['shadowActive']}`,
    },
    '&:disabled, &.Mui-disabled': {
      backgroundColor: colors['backgroundDisabled'],
      color: colors['colorDisabled'],
      boxShadow: `inset 0px 0px 0px 1px ${colors['shadowDisabled']}`,
    },
  };
};

export const createButtonTheme = (theme: Theme): Partial<Theme> => {
  return {
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            borderWidth: 0,
            [theme.breakpoints.up('lg')]: {
              fontSize: 16,
              lineHeight: 1.62,
              letterSpacing: '-0.015em',
            },
            '&.Mui-disabled': {
              '&[type="submit"][aria-disabled="true"]': {
                pointerEvents: 'auto',
              },
            },
            variants: [
              {
                props: {
                  variant: 'standard',
                  color: 'primary',
                },
                style: {
                  ...createButtonStates(theme.palette.button.primary),
                  '&, .if6 &': {
                    color: theme.palette.button.primary['color'],
                  },
                },
              },
              {
                props: {
                  variant: 'standard',
                  color: 'secondary',
                },
                style: createButtonStates(theme.palette.button.secondary),
              },
              {
                props: {
                  variant: 'standard',
                  color: 'tertiary',
                },
                style: createButtonStates(theme.palette.button.tertiary),
              },
              {
                props: {
                  variant: 'tab',
                },
                style: {
                  minWidth: 'auto',
                  backgroundColor: 'transparent',
                  border: 0,
                  color: theme.palette.primary['contrastText'],
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    bottom: '-2px',
                    height: '2px',
                    opacity: 0,
                    transition: theme.transitions.create('opacity', {
                      duration: '0.26s',
                    }),
                    backgroundColor: theme.palette.primary['contrastText'],
                    width: '100%',
                  },
                  '&:active, &[aria-current="page"], &[aria-current="location"], &[aria-selected="true"]': {
                    backgroundColor: 'transparent',
                    '&::after': {
                      opacity: 1,
                    },
                  },
                  '&:hover': {
                    backgroundColor: 'transparent',
                    '&::after': {
                      opacity: 1,
                    },
                  },
                  '&:focus-visible': {
                    backgroundColor: 'transparent',
                    '&::after': {
                      opacity: 1,
                    },
                  },
                  '&:disabled, &.Mui-disabled': {
                    color: theme.palette.grey[600],
                    backgroundColor: 'transparent',
                    borderColor: 'transparent',
                  },
                },
              },
              {
                props: {
                  variant: 'inverted',
                  color: 'secondary',
                },
                style: {
                  color: theme.palette.text['secondary'],
                },
              },
              {
                props: {
                  size: 'header',
                },
                style: {
                  paddingTop: theme.spacing(4),
                  paddingBottom: theme.spacing(4),
                  paddingLeft: theme.spacing(5),
                  paddingRight: theme.spacing(5),
                  borderRadius: '40px',
                  [theme.breakpoints.up('lg')]: {
                    paddingTop: theme.spacing(2),
                    paddingBottom: theme.spacing(2),
                    paddingLeft: theme.spacing(6),
                    paddingRight: theme.spacing(6),
                  },
                  '& .MuiButton-startIcon': {
                    marginLeft: theme.spacing(0),
                    marginRight: theme.spacing(4),
                  },
                },
              },
              {
                props: {
                  size: 'small',
                },
                style: {
                  paddingTop: theme.spacing(2),
                  paddingBottom: theme.spacing(2),
                  paddingLeft: theme.spacing(6),
                  paddingRight: theme.spacing(6),
                  borderRadius: '40px',
                },
              },
              {
                props: {
                  size: 'medium',
                },
                style: {
                  paddingTop: theme.spacing(4),
                  paddingBottom: theme.spacing(4),
                  paddingLeft: theme.spacing(8),
                  paddingRight: theme.spacing(8),
                  [theme.breakpoints.up('lg')]: {
                    paddingTop: theme.spacing(5),
                    paddingBottom: theme.spacing(5),
                    paddingLeft: theme.spacing(9),
                    paddingRight: theme.spacing(9),
                  },
                  borderRadius: '90px',
                },
              },
              {
                props: {
                  size: 'large',
                },
                style: {
                  // TODO: implement when required. figma has no version yet.
                  paddingTop: theme.spacing(5),
                  paddingBottom: theme.spacing(5),
                  paddingLeft: theme.spacing(7),
                  paddingRight: theme.spacing(7),
                  borderRadius: '90px',
                },
              },
              {
                props: {
                  variant: 'tab',
                  size: 'small',
                },
                style: {
                  padding: 0,
                  marginTop: theme.spacing(2),
                  marginBottom: theme.spacing(2),
                  marginLeft: theme.spacing(6),
                  marginRight: theme.spacing(6),
                  borderRadius: 0,
                },
              },
              {
                props: {
                  variant: 'tab',
                  size: 'medium',
                },
                style: {
                  padding: 0,
                  marginTop: theme.spacing(4),
                  marginBottom: theme.spacing(4),
                  marginLeft: theme.spacing(8),
                  marginRight: theme.spacing(8),
                  [theme.breakpoints.up('lg')]: {
                    padding: 0,
                    marginTop: theme.spacing(5),
                    marginBottom: theme.spacing(5),
                    marginLeft: theme.spacing(9),
                    marginRight: theme.spacing(9),
                  },
                  borderRadius: 0,
                },
              },
              {
                props: {
                  variant: 'tab',
                  size: 'large',
                },
                style: {
                  padding: 0,
                  marginTop: theme.spacing(5),
                  marginBottom: theme.spacing(5),
                  marginLeft: theme.spacing(7),
                  marginRight: theme.spacing(7),
                  borderRadius: 0,
                },
              },
              {
                props: {
                  variant: 'category',
                },
                style: {
                  backgroundColor: 'transparent',
                  whiteSpace: 'nowrap',
                  '&, .if6 &': {
                    color: theme.palette.secondary.darker,
                    paddingTop: theme.spacing(1), //Wurden wegen Focus benötigt --> ursprünglich 0
                    paddingBottom: theme.spacing(1),
                    paddingLeft: theme.spacing(1),
                    paddingRight: theme.spacing(1),
                    [theme.breakpoints.up('lg')]: {
                      paddingTop: theme.spacing(3),
                      paddingBottom: theme.spacing(3),
                      paddingLeft: theme.spacing(6),
                      paddingRight: theme.spacing(6),
                    },
                  },
                  '& .MuiButton-startIcon': {
                    marginLeft: theme.spacing(0),
                    marginRight: theme.spacing(2),

                    [theme.breakpoints.up('lg')]: {
                      marginRight: theme.spacing(3),
                    },
                  },
                  '&:not(:hover) .MuiAvatar-root': {
                    backgroundColor: theme.palette.grey['300'],
                  },
                  '&:focus-visible': {
                    boxShadow: `inset 0px 0px 0px 1px ${theme.palette.action.focus}`,
                    outline: `2px solid white`,
                    outlineOffset: '-3px',
                  },
                },
              },
              {
                props: {
                  fullWidth: true,
                },
                style: {
                  display: 'flex',
                  width: '100%',
                },
              },
            ],
          },
          endIcon: {
            marginLeft: theme.spacing(3),
          },
          startIcon: {
            marginRight: theme.spacing(3),
          },
        },
        defaultProps: {
          variant: 'standard',
          color: 'primary',
          disableElevation: true,
          disableFocusRipple: true,
          disableRipple: true,
        },
      },
    },
  };
};
